// Keep in sync with server/src/utils/whatnot-listing-details.js (compared in whatnot-mirrors.test.js).

/**
 * The details of a Whatnot listing that a Voolist listing can answer, and what a Whatnot listing's
 * details say about the item.
 *
 * A Whatnot listing's details are its answers to its category's attributes (whatnot-taxonomy.js), stored
 * as `whatnotAspects.attributes` = { [attributeId]: answer }. Voolist pre-fills the ones its own fields can
 * answer — condition, brand, colour, colourway and size — each in the words that category's attribute
 * uses, and only ever an answer the attribute accepts: a value from its list, or free text where it takes
 * free text. Everything else is the seller's to answer.
 */

import { toColor, fromColor } from './color-mapper';
import { toWhatnotCondition, fromWhatnotCondition } from './condition-mapper';
import { sizeScaleOf, offeredValue, defaultHazmatOf, attributesOfCategory, categoryAttributeAbout } from './whatnot-taxonomy';

// The letter Whatnot writes after a shoe size, for whose shoe it is.
const SHOE_SIZE_LETTER = { women: 'W', men: 'M' };

/** The attribute's own spelling of `answer`, or the answer itself where the attribute takes free text. */
const acceptedAnswer = (attribute, answer) => {
  if (!attribute || answer == null || String(answer).trim() === '') return null;
  return offeredValue(attribute, answer) || (attribute.customValues ? String(answer).trim() : null);
};

/**
 * Whose shoes a canonical category key describes, for the letter after a Whatnot shoe size: women's
 * (female), else men's — Whatnot's own default for an adult sneaker. Children's shoes take sizes Voolist
 * does not map.
 */
const shoeWearerOf = (canonicalKey) => {
  if (!canonicalKey) return 'men';
  if (canonicalKey.startsWith('kids::')) return null;
  return canonicalKey.startsWith('female') ? 'women' : 'men';
};

/**
 * The answer to the category's size question for a size another marketplace's listing had, as the
 * cross-platform mapper suggests it (US sizes: "9", "XL", "2XL", "OS").
 * @returns {{ attributeId: number, answer: string }|null}
 */
export const sizeDetail = (categoryId, suggestedSize, canonicalKey) => {
  const attribute = categoryAttributeAbout(categoryId, 'size');
  if (!attribute || !suggestedSize) return null;
  const scale = sizeScaleOf(attribute);
  let answer = null;
  if (scale === 'footwear') {
    const wearer = shoeWearerOf(canonicalKey);
    answer = wearer ? offeredValue(attribute, `${suggestedSize}${SHOE_SIZE_LETTER[wearer]}`) : null;
  } else if (scale === 'clothing') {
    answer = offeredValue(attribute, suggestedSize);
  }
  return answer ? { attributeId: attribute.id, answer } : null;
};

/**
 * The details a listing's own fields answer for a Whatnot category: { [attributeId]: answer }. A field the
 * listing lacks, or one the category's attribute has no accepted answer for, is left out.
 *
 * @param {number|string} categoryId
 * @param {Object} listing - its canonical fields: condition, brand, primaryColor, secondaryColor
 * @param {Object} [size] - a size another marketplace's listing had: { suggestedSize, canonicalKey }
 * @returns {Object<number, string>}
 */
export const detailsFromListing = (categoryId, listing, size = {}) => {
  const details = {};
  const answer = (topic, value) => {
    const attribute = categoryAttributeAbout(categoryId, topic);
    const accepted = acceptedAnswer(attribute, value);
    if (accepted) details[attribute.id] = accepted;
  };

  const conditionAttribute = categoryAttributeAbout(categoryId, 'condition');
  const condition = toWhatnotCondition(listing?.condition, conditionAttribute);
  if (condition) details[conditionAttribute.id] = condition;

  answer('brand', listing?.brand);

  const primary = listing?.primaryColor ? toColor(listing.primaryColor, 'whatnot') : null;
  const secondary = listing?.secondaryColor ? toColor(listing.secondaryColor, 'whatnot') : null;
  const colorAttribute = categoryAttributeAbout(categoryId, 'color');
  const color = offeredValue(colorAttribute, primary);
  if (color) details[colorAttribute.id] = color;
  // A sneaker's colourway reads "Black/White": its main colour, then the second.
  answer('colorway', primary && secondary && secondary !== primary ? `${primary}/${secondary}` : primary);

  const sized = sizeDetail(categoryId, size.suggestedSize, size.canonicalKey);
  if (sized) details[sized.attributeId] = sized.answer;

  return details;
};

/**
 * What a Whatnot listing's details say about the item, in Voolist's own fields: { condition, brand,
 * primaryColor, secondaryColor }, each null when the details do not say it in words Voolist reads.
 */
export const listingFieldsFromDetails = (categoryId, attributes) => {
  const answerTo = (topic) => {
    const attribute = categoryAttributeAbout(categoryId, topic);
    const answer = attribute ? attributes?.[attribute.id] : null;
    return { attribute, answer: answer == null || answer === '' ? null : String(answer).trim() };
  };

  const condition = answerTo('condition');
  const brand = answerTo('brand');
  const color = answerTo('color');
  const [firstColorway, secondColorway] = (answerTo('colorway').answer || '').split('/').map((part) => part.trim());

  return {
    condition: condition.answer ? fromWhatnotCondition(condition.answer, condition.attribute) : null,
    brand: brand.answer,
    primaryColor: (color.answer && fromColor(color.answer, 'whatnot')) || (firstColorway && fromColor(firstColorway, 'whatnot')) || null,
    secondaryColor: (secondColorway && fromColor(secondColorway, 'whatnot')) || null,
  };
};

/**
 * The answers among `attributes` that this category asks. Answers given in another category carry over
 * only where the new category asks the same question — the fashion categories share their condition,
 * size and brand questions; a sneaker's colourway means nothing to a handbag.
 */
export const detailsForCategory = (categoryId, attributes) => {
  const asked = new Set(attributesOfCategory(categoryId).map((attribute) => String(attribute.id)));
  return Object.fromEntries(Object.entries(attributes || {}).filter(([attributeId]) => asked.has(String(attributeId))));
};

/**
 * A Whatnot listing's answers as they are sent to Whatnot: the seller's own, with the details the listing's
 * own fields answer (condition, brand, colours) where the seller gave none, and the category's answer to
 * "Hazardous materials" when the seller gave none. Worked out when the listing is sent rather than stored
 * when the form opens, so a later change to the listing — its condition, say — still reaches Whatnot. A
 * detail the seller cleared (null) is not filled in again.
 *
 * @param {Object|null} aspects - the listing's `whatnotAspects`
 * @param {Object} listing - its canonical fields: condition, brand, primaryColor, secondaryColor
 * @returns {Object|null}
 */
export const whatnotAspectsAsSent = (aspects, listing) => {
  if (aspects?.categoryId == null || aspects.categoryId === '') return aspects;
  return {
    ...aspects,
    attributes: { ...detailsFromListing(aspects.categoryId, listing), ...(aspects.attributes || {}) },
    hazmat: aspects.hazmat || defaultHazmatOf(aspects.categoryId),
  };
};
